import { useTranslation } from 'react-i18next';
import { StyleSheet, View, ViewStyle } from 'react-native';

import colors from 'config/colors';
import {
  ActionButtonOrder,
  HasUnacceptChangesLabel,
} from 'modules/ChefScene/components/ActionButton';
import { RouteNames } from 'navigation/linkingConfig';
import { useAppSelector } from 'store/hooks';
import { NotificationsSelectors } from 'store/Notifications';
import {
  ItemPreparationStatuses,
  OrderInternalStatuses,
} from 'store/Orders/enums';
import { checkIfScheduledOrder } from 'store/Orders/utils';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';
import { getLabelKeyForOrderKind } from 'util/helpers';
import {
  OrderStartPreparationStatusBadge,
  Spacer,
  Text,
  VerticalDivider,
} from 'components';

import { BadgeSection } from './BadgeSection';
import { InfoSection } from './InfoSection';
import type { Order, OrderItem } from 'store/Orders/types';

type OrderHeaderProps = {
  order: Order;
  items: OrderItem[];
  orderInternalStatus: OrderInternalStatuses;
  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;
  onPressedAction?: () => void;
  style?: ViewStyle;
};

export const OrderHeader = ({
  order,
  items,
  orderInternalStatus,
  onStatusChanged,
  onPressedAction,
  style,
}: OrderHeaderProps) => {
  const { t } = useTranslation();
  const logEvent = useLogEventCallback();

  const isScheduledFutureOrder = checkIfScheduledOrder(order);

  const featureFlags = useAppSelector(UserSelectors.selectFeatureFlags);

  const hasUnacceptedNotifications = useAppSelector((state) =>
    NotificationsSelectors.selectOrderHasUnattended(state, order.uuid),
  );
  const { shouldDisplayActionButtonHeader } = useAppSelector(
    SettingSelectors.selectOrderDetailsInterfaceSettings,
  );

  const showActionButton =
    orderInternalStatus !== OrderInternalStatuses.CANCELLED &&
    (hasUnacceptedNotifications ||
      (shouldDisplayActionButtonHeader &&
        orderInternalStatus !== OrderInternalStatuses.PREPARED));

  const getStatusChangeCallback =
    (params?: Dict) =>
    (
      statusToUpdate: ItemPreparationStatuses,
      itemsToUpdate: OrderItem[],
      duration: number,
      startDate: Date,
      endDate: Date,
    ) => {
      onPressedAction?.();
      logEvent(
        AnalyticsEvents.itemStatusChanged({
          isScheduled: isScheduledFutureOrder,
          screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
          status: statusToUpdate,
          orderId: order.uuid,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          executionTime: duration,
          ...params,
        }),
      );

      for (const item of itemsToUpdate) {
        onStatusChanged?.({
          startDate,
          endDate,
          status: statusToUpdate,
          itemId: item.uuid,
          mode: 'group',
          orderId: item.order_uuid,
          screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
        });
      }
    };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.ui.orderKinds[order.kind],
        },
        style,
      ]}
    >
      <InfoSection order={order} items={items} />
      <VerticalDivider style={{ borderLeftColor: 'white' }} />
      <View style={{ flex: 1, gap: 2, alignSelf: 'stretch' }}>
        <View style={{ gap: 8 }}>
          <BadgeSection order={order} />
          <Text color="white" font="bold" align="center">
            {t(getLabelKeyForOrderKind(order.kind))}
          </Text>

          {featureFlags.kds_enabled_traffic_light_system &&
            orderInternalStatus ===
              OrderInternalStatuses.NOT_BEING_PREPARED && (
              <OrderStartPreparationStatusBadge
                status={order.start_preparation_status}
                iconSize={18}
                textSize={14}
              />
            )}
        </View>
        <Spacer />
        {showActionButton && (
          <View
            style={{
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {hasUnacceptedNotifications ? (
              <HasUnacceptChangesLabel color={'white'} />
            ) : shouldDisplayActionButtonHeader &&
              orderInternalStatus !== OrderInternalStatuses.PREPARED ? (
              <ActionButtonOrder
                includePrintButton
                order={order}
                items={items}
                isScheduled={isScheduledFutureOrder}
                onPressFinish={getStatusChangeCallback({ print: false })}
                onPressFinishAndPrint={getStatusChangeCallback({
                  print: true,
                })}
              />
            ) : null}
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    padding: 8,
  },
});
